import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, LogOut, BatteryCharging, FileText, GitCompare, Users, Share2, Building2, ArrowRight, Compass, Pencil, TrendingUp, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { downloadDiscoveryPdf } from "@/lib/downloads";
import ProfileEditModal from "@/components/ProfileEditModal";
import { PERSONA_META, useLang, LangToggle } from "@/components/landing/i18n";

const DASH_UI = {
  en: {
    gateTitle: "Your dashboard awaits",
    gateCopy: "Sign in to see your reports, batteries, team and next steps in one place.",
    gateBtn: "Sign in / Create free account",
    editProfile: "Edit profile",
    signOut: "Sign out",
    greeting: (name) => `Hey, ${name}`,
    statFullReports: "Full reports",
    statDiscovery: "Discovery",
    statBattery: "Battery in progress",
    statOrg: "Organization",
    statInsight: "First insight",
    inProgress: "In progress",
    yes: "Yes",
    done: "Done",
    resumeDiscovery: "Resume Discovery",
    discoveryProfile: "Discovery profile",
    discoveryAssessment: "Discovery assessment",
    discoverySub: "190 questions across five psychometric modules",
    aptitude: "aptitude",
    progress: "Progress timeline",
    progressSub: "Compare Discovery retakes over time",
    resumeBattery: "Resume battery",
    takeBattery: "Take the battery",
    batteryBusy: "You have an unfinished session",
    batterySub: "Student or professional — 24 adaptive questions",
    myReports: "My reports",
    reportsSoFar: (n) => `${n} full report${n === 1 ? "" : "s"} so far`,
    thenVsNow: "Then vs now",
    compareTwo: "Compare your two batteries",
    unlockCompare: "Take both batteries to unlock",
    myTeam: "My team",
    createOrg: "Create or join an organization",
    latestReport: "Latest report",
    professional: "Professional",
    student: "Student",
    aptitudeLabel: "Aptitude",
    downloadPdf: "Download PDF",
    shareCard: "Share card",
    allReports: "All reports",
    discoveryStrip: "BrightIQ Discovery · 5 modules",
    topCluster: "Top cluster",
    viewProfile: "View profile",
    inviteCode: "invite code",
    institutionLinked: "Linked institution",
    institutionJoinTitle: "Linked to a school, college or company?",
    institutionJoinSub: "Enter the join code they shared so your reports show up on their portal.",
    institutionCodePlaceholder: "Institution join code",
    institutionJoinBtn: "Link",
    institutionJoined: (name) => `Linked to ${name}`,
    institutionInvalid: "Invalid institution code",
    pdfOk: "Discovery PDF downloaded",
    pdfFail: "PDF download failed",
    plans: (loc) => ({ Domestic: "Domestic plans", International: "International plans", "Job Seeker": "Job seeker" }[loc] || loc),
    dateLocale: "en-GB",
  },
  hi: {
    gateTitle: "आपका डैशबोर्ड इंतज़ार कर रहा है",
    gateCopy: "अपनी रिपोर्ट, बैटरी, टीम और अगले कदम एक जगह देखने के लिए साइन इन करें।",
    gateBtn: "साइन इन / मुफ़्त खाता बनाएँ",
    editProfile: "प्रोफ़ाइल बदलें",
    signOut: "साइन आउट",
    greeting: (name) => `नमस्ते, ${name}`,
    statFullReports: "पूरी रिपोर्ट",
    statDiscovery: "डिस्कवरी",
    statBattery: "बैटरी जारी",
    statOrg: "संगठन",
    statInsight: "पहली झलक",
    inProgress: "जारी",
    yes: "हाँ",
    done: "हो गया",
    resumeDiscovery: "डिस्कवरी जारी रखें",
    discoveryProfile: "डिस्कवरी प्रोफ़ाइल",
    discoveryAssessment: "डिस्कवरी आकलन",
    discoverySub: "पाँच साइकोमेट्रिक मॉड्यूल में 190 प्रश्न",
    aptitude: "योग्यता",
    progress: "प्रगति टाइमलाइन",
    progressSub: "समय के साथ डिस्कवरी रीटेक की तुलना करें",
    resumeBattery: "बैटरी जारी रखें",
    takeBattery: "बैटरी दें",
    batteryBusy: "आपका एक सेशन अधूरा है",
    batterySub: "विद्यार्थी या प्रोफेशनल — 24 अनुकूल प्रश्न",
    myReports: "मेरी रिपोर्ट",
    reportsSoFar: (n) => `अब तक ${n} पूरी रिपोर्ट`,
    thenVsNow: "तब बनाम अब",
    compareTwo: "अपनी दोनों बैटरी की तुलना करें",
    unlockCompare: "अनलॉक के लिए दोनों बैटरी दें",
    myTeam: "मेरी टीम",
    createOrg: "संगठन बनाएँ या जुड़ें",
    latestReport: "नवीनतम रिपोर्ट",
    professional: "प्रोफेशनल",
    student: "विद्यार्थी",
    aptitudeLabel: "योग्यता",
    downloadPdf: "PDF डाउनलोड करें",
    shareCard: "कार्ड साझा करें",
    allReports: "सभी रिपोर्ट",
    discoveryStrip: "BrightIQ डिस्कवरी · 5 मॉड्यूल",
    topCluster: "टॉप क्लस्टर",
    viewProfile: "प्रोफ़ाइल देखें",
    inviteCode: "आमंत्रण कोड",
    institutionLinked: "जुड़ा संस्थान",
    institutionJoinTitle: "किसी स्कूल, कॉलेज या कंपनी से जुड़ना है?",
    institutionJoinSub: "उनके द्वारा साझा किया गया जॉइन कोड डालें ताकि आपकी रिपोर्ट उनके पोर्टल पर दिखे।",
    institutionCodePlaceholder: "संस्थान जॉइन कोड",
    institutionJoinBtn: "जोड़ें",
    institutionJoined: (name) => `${name} से जुड़ गए`,
    institutionInvalid: "अमान्य संस्थान कोड",
    pdfOk: "डिस्कवरी PDF डाउनलोड हुई",
    pdfFail: "PDF डाउनलोड विफल",
    plans: (loc) => ({ Domestic: "भारत में पढ़ाई की योजना", International: "विदेश में पढ़ाई की योजना", "Job Seeker": "नौकरी की तलाश" }[loc] || loc),
    dateLocale: "hi-IN",
  },
};

export default function DashboardPage() {
  const { lang } = useLang();
  const dui = DASH_UI[lang] || DASH_UI.en;
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [data, setData] = useState(null);
  const [instCode, setInstCode] = useState("");
  const [instBusy, setInstBusy] = useState(false);

  const loadDashboard = () => {
    authFetch(`/api/me/dashboard?lang=${lang}`).then(async (r) => {
      if (r.status === 401) {
        clearAuth();
        setUser(null);
        return;
      }
      setData(await r.json());
    });
  };

  useEffect(() => {
    if (!user) return;
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, lang]);

  const joinInstitution = async (e) => {
    e.preventDefault();
    if (!instCode.trim()) return;
    setInstBusy(true);
    try {
      const res = await authFetch("/api/me/institution/join", { method: "POST", body: JSON.stringify({ code: instCode.trim() }) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : dui.institutionInvalid);
      toast.success(dui.institutionJoined(d.institution.name));
      setInstCode("");
      loadDashboard();
    } catch (err) {
      toast.error(err.message || dui.institutionInvalid);
    }
    setInstBusy(false);
  };

  const reports = data?.reports || [];
  const latestReport = reports[0];
  const tracks = new Set(reports.map((r) => r.track || "student"));

  const downloadDiscovery = async () => {
    if (!data?.discoveryReport?.id) return;
    try {
      await downloadDiscoveryPdf(data.discoveryReport.id, undefined, lang);
      toast.success(dui.pdfOk);
    } catch (err) {
      toast.error(err.message || dui.pdfFail);
    }
  };


  const actions = [
    { to: "/discovery", Icon: Compass, color: "#F5A524", title: data?.discoveryInProgress ? dui.resumeDiscovery : data?.discoveryReport ? dui.discoveryProfile : dui.discoveryAssessment, sub: data?.discoveryReport ? `${data.discoveryReport.archetype} · ${data.discoveryReport.aptitude_overall}% ${dui.aptitude}` : dui.discoverySub, testid: "dash-action-discovery" },
    { to: "/progress", Icon: TrendingUp, color: "#17B6E0", title: dui.progress, sub: dui.progressSub, testid: "dash-action-progress" },
    { to: "/assessment", Icon: BatteryCharging, color: "#1F5BFF", title: data?.inProgress ? dui.resumeBattery : dui.takeBattery, sub: data?.inProgress ? dui.batteryBusy : dui.batterySub, testid: "dash-action-battery" },
    { to: "/reports", Icon: FileText, color: "#E84393", title: dui.myReports, sub: dui.reportsSoFar(reports.length), testid: "dash-action-reports" },
    { to: "/compare", Icon: GitCompare, color: "#7C3AED", title: dui.thenVsNow, sub: tracks.size > 1 ? dui.compareTwo : dui.unlockCompare, testid: "dash-action-compare" },
    { to: "/team", Icon: Users, color: "#3AA64B", title: dui.myTeam, sub: data?.org ? data.org.name : dui.createOrg, testid: "dash-action-team" },
  ];

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="dashboard-page">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="dash-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        {user && (
          <div className="flex items-center gap-4">
            <LangToggle />
            <button onClick={() => setEditOpen(true)} data-testid="dash-edit-profile-button" className="flex items-center gap-1.5 text-xs font-semibold text-ink/60 hover:text-iqblue transition-colors">
              <Pencil className="h-3.5 w-3.5" /> {dui.editProfile}
            </button>
            <button onClick={() => { clearAuth(); setUser(null); setData(null); }} data-testid="dash-logout-button" className="flex items-center gap-1.5 text-xs font-semibold text-ink/50 hover:text-iqpink transition-colors">
              <LogOut className="h-3.5 w-3.5" /> {dui.signOut}
            </button>
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="dash-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqblue/10 items-center justify-center"><Lock className="h-6 w-6 text-iqblue" /></span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-6">{dui.gateTitle}</h1>
            <p className="mt-3 text-ink/60 text-sm sm:text-base max-w-md mx-auto">{dui.gateCopy}</p>
            <button onClick={() => setAuthOpen(true)} data-testid="dash-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {dui.gateBtn}
            </button>
          </div>
        )}

        {user && !data && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="dash-loading" /></div>}

        {user && data && (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
              <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight" data-testid="dash-greeting">
                {dui.greeting(data.user.name.split(" ")[0])}
              </h1>
              <p className="mt-2 text-sm text-ink/55">
                {[data.user.grade === "Career Professional" && lang === "hi" ? "करियर प्रोफेशनल" : data.user.grade, data.user.school, data.user.study_location && dui.plans(data.user.study_location), data.user.industry].filter(Boolean).join(" · ")}
              </p>
            </motion.div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                [reports.length, dui.statFullReports, "full-reports"],
                [data.discoveryReport ? data.discoveryReport.archetype : data.discoveryInProgress ? dui.inProgress : "—", dui.statDiscovery, "discovery"],
                [data.inProgress ? dui.yes : "—", dui.statBattery, "battery-in-progress"],
                [data.org ? data.org.name : "—", dui.statOrg, "organization"],
                [data.teaser ? (PERSONA_META[data.teaser.top]?.[lang]?.name || PERSONA_META[data.teaser.top]?.en.name || dui.done) : "—", dui.statInsight, "first-insight"],
              ].map(([v, l, id]) => (
                <div key={id} className="rounded-3xl bg-cream border border-ink/10 p-5" data-testid={`dash-stat-${id}`}>
                  <div className="font-display font-bold text-lg leading-tight truncate">{v}</div>
                  <div className="text-xs text-ink/50 mt-1">{l}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {actions.map((a, i) => (
                <motion.div key={a.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                  <Link to={a.to} data-testid={a.testid} className="group block rounded-3xl bg-cream border border-ink/10 border-t-4 p-6 h-full transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-20px_rgba(10,22,51,0.2)]" style={{ borderTopColor: a.color }}>
                    <span className="inline-flex h-11 w-11 rounded-2xl items-center justify-center transition-transform duration-500 group-hover:scale-110" style={{ background: `${a.color}18` }}>
                      <a.Icon className="h-5 w-5" style={{ color: a.color }} />
                    </span>
                    <div className="font-display font-bold text-base tracking-tight mt-4">{a.title}</div>
                    <p className="mt-1.5 text-xs text-ink/55 leading-relaxed">{a.sub}</p>
                  </Link>
                </motion.div>
              ))}
            </div>

            {latestReport && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.5 }} className="mt-8 rounded-3xl bg-ink text-paper p-8 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="dash-latest-report">
                <div className="flex-1">
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-iqcyan">{dui.latestReport} · {(latestReport.track || "student") === "professional" ? dui.professional : dui.student}{latestReport.industry ? ` · ${latestReport.industry}` : ""}</div>
                  <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-2" style={{ color: PERSONA_META[latestReport.persona].color }}>
                    {PERSONA_META[latestReport.persona][lang]?.name || PERSONA_META[latestReport.persona].en.name}
                  </div>
                  <p className="mt-2 text-sm text-paper/60">{dui.aptitudeLabel} {latestReport.aptPct}% · {new Date(latestReport.created_at).toLocaleDateString(dui.dateLocale, { day: "2-digit", month: "short", year: "numeric" })}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid="dash-pdf-button"
                    onClick={async () => {
                      try {
                        const res = await authFetch(`/api/reports/${latestReport.id}/pdf?lang=${lang}`);
                        if (!res.ok) throw new Error();
                        const blob = await res.blob();
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = "brightiq-report.pdf";
                        a.click();
                        URL.revokeObjectURL(a.href);
                      } catch {
                        toast.error(dui.pdfFail);
                      }
                    }}
                    className="flex items-center gap-2 bg-iqamber text-ink font-semibold text-sm px-6 py-3.5 rounded-full hover:brightness-110 transition-all duration-300"
                  >
                    <FileText className="h-4 w-4" /> {dui.downloadPdf}
                  </button>
                  <Link to={`/r/${latestReport.id}`} data-testid="dash-share-card" className="flex items-center gap-2 bg-paper text-ink font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-iqcyan transition-colors duration-300">
                    <Share2 className="h-4 w-4" /> {dui.shareCard}
                  </Link>
                  <Link to="/reports" data-testid="dash-all-reports" className="flex items-center gap-2 border border-white/20 font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-white/10 transition-colors duration-300">
                    {dui.allReports} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            )}

            {data.discoveryReport && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42, duration: 0.5 }} className="mt-6 rounded-3xl bg-cream border border-ink/10 p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="dash-discovery-report">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-iqamber">{dui.discoveryStrip}</div>
                  <div className="font-display font-bold text-2xl tracking-tight mt-2">{data.discoveryReport.archetype}</div>
                  <p className="mt-1.5 text-sm text-ink/55">{dui.topCluster}: {data.discoveryReport.clusters?.[0]?.name} · {dui.aptitudeLabel} {data.discoveryReport.aptitude_overall}%</p>
                </div>
                <div className="flex flex-wrap gap-3 shrink-0">
                  <button onClick={downloadDiscovery} data-testid="dash-discovery-pdf" className="flex items-center gap-2 bg-iqamber text-ink font-semibold text-sm px-6 py-3.5 rounded-full hover:brightness-110 transition-all duration-300">
                    <FileText className="h-4 w-4" /> PDF
                  </button>
                  <Link to={`/discovery/report/${data.discoveryReport.id}`} data-testid="dash-discovery-view" className="flex items-center gap-2 bg-ink text-paper font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-iqamber hover:text-ink transition-colors duration-300">
                    {dui.viewProfile} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            )}

            {data.org && (
              <Link to="/team" data-testid="dash-org-strip" className="mt-6 flex items-center justify-between rounded-3xl bg-cream border border-ink/10 px-7 py-5 hover:-translate-y-0.5 transition-all duration-300">
                <span className="flex items-center gap-3 text-sm font-semibold"><Building2 className="h-4 w-4 text-iqgreen" /> {data.org.name} — {dui.inviteCode} <span className="font-mono tracking-[0.2em]">{data.org.code}</span></span>
                <ArrowRight className="h-4 w-4 text-ink/40" />
              </Link>
            )}

            {data.institution ? (
              <div data-testid="dash-institution-strip" className="mt-6 flex items-center gap-3 rounded-3xl bg-cream border border-ink/10 px-7 py-5">
                <GraduationCap className="h-4 w-4 text-iqblue shrink-0" />
                <span className="text-sm font-semibold">{dui.institutionLinked}: {data.institution.name}</span>
                <span className="text-xs text-ink/45 font-mono tracking-[0.2em]">{data.institution.code}</span>
              </div>
            ) : (
              <form onSubmit={joinInstitution} data-testid="dash-institution-join" className="mt-6 rounded-3xl bg-cream border border-ink/10 px-7 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-2"><GraduationCap className="h-4 w-4 text-iqblue" /> {dui.institutionJoinTitle}</div>
                  <p className="text-xs text-ink/50 mt-1">{dui.institutionJoinSub}</p>
                </div>
                <div className="flex gap-2.5">
                  <input value={instCode} onChange={(e) => setInstCode(e.target.value.toUpperCase())} placeholder={dui.institutionCodePlaceholder} data-testid="dash-institution-code-input"
                    className="rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-mono tracking-[0.2em] uppercase outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35 placeholder:tracking-normal placeholder:font-body w-48" />
                  <button type="submit" disabled={instBusy || !instCode.trim()} data-testid="dash-institution-join-button" className="bg-ink text-paper font-semibold text-xs px-6 rounded-full hover:bg-iqblue disabled:opacity-40 transition-colors">
                    {instBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : dui.institutionJoinBtn}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
      <ProfileEditModal open={editOpen} onClose={() => setEditOpen(false)} user={data?.user || user} onSaved={(u) => { setUser(u); setData((d) => d ? { ...d, user: { ...d.user, ...u } } : d); }} />
    </div>
  );
}
